import {
	Magnite_Vendors,
	Required_Purposes,
} from "@/config/reach/common/constants/consent";
import { gptStore } from "@/store/gptStore";
import { headerBiddingStore } from "@/store/headerBiddingStore";
import {
	isAdServerTargeting,
	isDisplaySlotConfig,
	isMagnitePBJSObj,
	isMagniteVideoSlotConfig,
} from "@/ts/guards";
import type { AuctionModule, MagniteModuleConfig } from "@/ts/module";
import type { UserSync } from "@/ts/prebid/types";
import type { VendorName } from "@/ts/storeTypes/headerBiddingTypes";
import type {
	AdSizesMagniteConfig,
	BidderSettings,
	DisplaySlotConfig,
	MagniteConfig,
	MagniteSize,
	MagniteSlotMapEntry,
	MagniteVideoSlotConfig,
	PbjsConfig,
} from "@/ts/types";
import { createDependencyLoader } from "@/utils/dependencyLoader";
import { appendHtmlToHead, appendHtmlToIframe } from "@/utils/dom";
import { getModules } from "@/utils/modules";
import { updatePrebidTargetingToStore } from "@/utils/prebidDataHelper";
import {
	filterSlotBySize,
	getSizeForSlot,
	removeSizeMapping,
	removeUnneededSizes,
} from "@/utils/slots";
import { magniteDMTag, magniteLinkTag } from "@/vendors/magniteDMTag";

export class MagniteDemandManager implements AuctionModule {
	private config: MagniteModuleConfig;
	name = "MagniteDemandManager" as const;

	constructor(config: MagniteModuleConfig) {
		this.config = config;
	}

	async init(): Promise<void> {
		const { actions } = headerBiddingStore.getState();
		actions.addVendor(this.name);

		const [consentModule] = await getModules(["Consent"]);
		const consented = await consentModule.run({
			vendorId: Magnite_Vendors,
			purposes: Required_Purposes,
		});
		if (!consented) {
			return;
		}
		const { runInIframe } = this.config;
		await magniteLoader.ensure(runInIframe);
	}

	async run(
		auctionId: number,
		cmdSlots: (DisplaySlotConfig | MagniteVideoSlotConfig)[],
		_timeout: number,
	) {
		// biome-ignore lint/suspicious/noConsole: temporary debug logging for flaky e2e
		console.log(
			`[MagniteDM] run() called — auctionId=${auctionId}, cmdSlots.length=${cmdSlots.length}`,
		);
		const { actions } = headerBiddingStore.getState();
		const [consentModule] = await getModules(["Consent"]);
		const consented = consentModule.run({
			vendorId: Magnite_Vendors,
			purposes: Required_Purposes,
			sync: true,
		});
		if (!consented) {
			actions.updateVendorAuctionStatus(auctionId, this.name, "failed");
			return;
		}
		const { runInIframe } = this.config;
		const win = await magniteLoader.ensure(runInIframe);
		const { userSync, adSizesMagnite, bidderSettings, pbjsConfig } =
			this.config;

		const displaySlotSizeMatchesConfig = filterSlotBySize(adSizesMagnite);

		const displaySlots = cmdSlots
			.filter(isDisplaySlotConfig)
			.filter(displaySlotSizeMatchesConfig);
		const videoSlots = cmdSlots.filter(isMagniteVideoSlotConfig);

		const hasVideoSlots = videoSlots.length > 0;
		const hasDisplaySlots = displaySlots.length > 0;

		// biome-ignore lint/suspicious/noConsole: temporary debug logging for flaky e2e
		console.log(
			`[MagniteDM] run() auctionId=${auctionId}, cmdSlots=${cmdSlots.length}, displaySlots=${displaySlots.length}, videoSlots=${videoSlots.length}`,
		);

		if (hasVideoSlots || hasDisplaySlots) {
			configurePbjs(win, userSync, pbjsConfig, bidderSettings);
		} else {
			// biome-ignore lint/suspicious/noConsole: temporary debug logging for flaky e2e
			console.log(
				`[MagniteDM] run() no slots after filtering — failing auction ${auctionId}`,
			);
			actions.updateVendorAuctionStatus(auctionId, this.name, "failed");
			return;
		}

		// video slot?
		const slotIds = displaySlots.map((slotCfg) => slotCfg.slotId);

		const { pageAdUnitPath } = gptStore.getState();
		const displaySlotsMagnite = prepareDisplaySlots(
			displaySlots,
			adSizesMagnite,
			pageAdUnitPath,
		);
		const videoSlotsMagnite = prepareVideoslots(videoSlots);
		// TODO <Bid[]>? or use :unknown because we don't care about the 'bids' object that is returned
		const requestBidsPromises: Promise<unknown>[] = [];
		actions.updateVendorAuctionStatus(auctionId, this.name, "running");

		if (hasVideoSlots && videoSlotsMagnite) {
			requestBidsPromises.push(
				new Promise((resolve) => {
					win.pbjs.que.push(() => {
						if (!isMagnitePBJSObj(win.pbjs)) {
							actions.updateVendorAuctionStatus(auctionId, this.name, "failed");
							return;
						}
						// TODO: get this fully working
						win.pbjs.rp.requestVideoBids({
							...videoSlotsMagnite,
							callback: resolve,
						});
					});
				}),
			);
		}
		if (hasDisplaySlots && displaySlotsMagnite) {
			requestBidsPromises.push(
				new Promise((resolve) => {
					win.pbjs.que.push(() => {
						// use typia to persuade ts that pbjs.rp prop now exists
						if (!isMagnitePBJSObj(win.pbjs)) {
							actions.updateVendorAuctionStatus(auctionId, this.name, "failed");
							return;
						}
						win.pbjs.rp.requestBids({
							...displaySlotsMagnite,
							callback: resolve,
						});
					});
				}),
			);
		}
		// altho Magnite returns the bids in the callback, we ignore it - we don't
		//   put the return val of the promises into a variable - because
		//   pbjs.getAdserverTargeting() gives us exactly the data we need and nothing else
		await Promise.all(requestBidsPromises);
		auctionCompletionCallback(win, auctionId, this.name)(slotIds);
	}
}

const auctionCompletionCallback =
	(win: Window, auctionId: number, vendorName: VendorName) =>
	(slotIds: string[]) => {
		const { actions } = headerBiddingStore.getState();
		// DM should have applied the targeting to the gptSlots already,
		//  assuming they already exist, but we write it to the store
		//  anyway, for consistency.
		const ast = win.pbjs.getAdserverTargeting();
		// validate 3rd-party data before touching it
		if (isAdServerTargeting(ast)) {
			updatePrebidTargetingToStore(auctionId, slotIds, ast);
		}

		actions.updateVendorAuctionStatus(auctionId, vendorName, "done");
	};

const prepareDisplaySlots = (
	displaySlots: DisplaySlotConfig[],
	adSizesMagnite: AdSizesMagniteConfig,
	pageAdUnitPath: string,
) => {
	const getBaseSlotCfgWithCustomMappingAndTargeting = (
		slotCfg: DisplaySlotConfig,
		pageAdUnitPath: string,
	) => ({
		divId: slotCfg.slotId,
		name: slotCfg.adUnitPath ?? pageAdUnitPath,
		customMappingParams: slotCfg.targeting,
		getTargeting(key: string) {
			return [this.customMappingParams[key]];
		},
		...(slotCfg.sizeMapping && { sizeMapping: slotCfg.sizeMapping }),
	});

	const getConfiguredSlotSize = (
		slotCfg: ReturnType<typeof getBaseSlotCfgWithCustomMappingAndTargeting>,
	) => ({
		...slotCfg,
		sizes: getSizeForSlot(slotCfg.divId, adSizesMagnite) as MagniteSize[],
	});

	const slotsMagnite: MagniteSlotMapEntry[] = displaySlots
		.map((slotCfg) =>
			getBaseSlotCfgWithCustomMappingAndTargeting(slotCfg, pageAdUnitPath),
		)
		.map(getConfiguredSlotSize)
		.map(removeUnneededSizes)
		.map(removeSizeMapping);

	return slotsMagnite.length > 0
		? {
				slotMap: slotsMagnite,
				divPatternMatching: true,
			}
		: null;
};

const prepareVideoslots = (videoSlots: MagniteVideoSlotConfig[]) => {
	// Magnite only supports single video slot per auction
	if (!videoSlots[0]) {
		return null;
	}
	const { videoAdUnitPath } = videoSlots[0];
	return {
		adSlotName: videoAdUnitPath,
		playerSize: [640, 480], // currentSize also exists. [606, 341] has also been used for some reason
	};
};

const configurePbjs = (
	win: Window,
	userSync: UserSync | undefined,
	pbjsConfig: PbjsConfig | undefined,
	bidderSettings: BidderSettings | undefined,
) => {
	const { ortb2 } = headerBiddingStore.getState().selectors.getAuctionData();
	const config: MagniteConfig = {
		ortb2,
		...(pbjsConfig || { enableTIDs: true }),
		...(userSync && { userSync }),
	};
	win.pbjs.que.push(() => {
		win.pbjs.bidderSettings = {
			...(win.pbjs.bidderSettings || {}),
			...bidderSettings,
		};
		win.pbjs.setConfig(structuredClone(config));
	});
};

// export the dependency-loader so we can reset it for unit testing
export const magniteLoader = createDependencyLoader<Window, [boolean]>(
	(runInIframe: boolean) => {
		// Skip magnite-link if runInIframe (only for reach sites, not third-party)
		if (!runInIframe) {
			appendHtmlToHead(magniteLinkTag);
		}

		const win = runInIframe
			? appendHtmlToIframe(magniteDMTag)
			: appendHtmlToHead(magniteDMTag);
		if (!win) {
			console.warn("Failed to initialize window context.");
		}
		win.pbjs = win.pbjs || { que: [] };
		return Promise.resolve(win);
	},
);
